import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type { StoredLevel } from '../types/gameApi';
import type { Difficulty } from '../types/game';

const LEVELS_KEY = ['game-levels'];

export function usePublishedLevels() {
  return useQuery({
    queryKey: LEVELS_KEY,
    queryFn: () => api.get<StoredLevel[]>('/game/levels'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllLevels() {
  return useQuery({
    queryKey: [...LEVELS_KEY, 'admin'],
    queryFn: () => api.get<StoredLevel[]>('/game/levels?admin=1'),
    staleTime: 30_000,
    // Poll every 5s while any level is still generating
    refetchInterval: (query) => {
      const levels = query.state.data as StoredLevel[] | undefined;
      return levels?.some((l) => l.status === 'generating') ? 5000 : false;
    },
  });
}

export function useGenerateLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (difficulty: Difficulty) =>
      api.post<StoredLevel>('/game/generate-level', { difficulty }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...LEVELS_KEY, 'admin'] }),
  });
}

export function useManageLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      levelId,
      action,
      publishAt,
    }: {
      levelId: string;
      action: 'publish' | 'schedule' | 'unpublish';
      publishAt?: string;
    }) => api.put<{ levelId: string; status: string }>(`/game/levels/${levelId}`, { action, publishAt }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEVELS_KEY });
      qc.invalidateQueries({ queryKey: [...LEVELS_KEY, 'admin'] });
    },
  });
}

// Deletes drafts from DB permanently; archives published/scheduled levels
export function useDeleteLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (levelId: string) =>
      api.delete<{ levelId: string; deleted?: boolean; status?: string }>(`/game/levels/${levelId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...LEVELS_KEY, 'admin'] }),
  });
}

// Increments totalCompletions counter (call when player solves or reveals solution)
export function useCompleteLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (levelId: string) =>
      api.post<{ levelId: string; counted: boolean }>(`/game/levels/${levelId}/complete`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: LEVELS_KEY }),
  });
}
